import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useCuestionarios } from './hooks/useCuestionarios';
import Loader from '../../components/UI/Loader';
import EmptyState from '../../components/UI/EmptyState';
import SearchInput from '../../components/UI/SearchInput';
import Modal from '../../components/UI/Modal';
import { toast } from 'react-toastify';

export default function CuestionariosListPage() {
  const navigate = useNavigate();
  const { listar, toggleActive, duplicar, eliminar } = useCuestionarios();
  const [grupos, setGrupos] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Confirm delete modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async (searchTerm = search) => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm.length >= 3) params.search = searchTerm;
      const { data } = await listar(params);
      setGrupos(data.results || []);
    } catch (error) {
      console.error('Error al cargar formularios:', error);
      toast.error('Error al cargar los formularios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchData(search), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleToggle = async (id) => {
    try {
      await toggleActive(id);
      fetchData();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicar(id);
      toast.success('Formulario duplicado exitosamente');
      fetchData();
    } catch (error) {
      console.error('Error al duplicar:', error);
      toast.error('Error al duplicar el formulario');
    }
  };

  const openDeleteConfirm = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await eliminar(deletingId);
      toast.success('Formulario eliminado exitosamente');
      setConfirmOpen(false);
      setDeletingId(null);
      fetchData();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el formulario');
    } finally {
      setDeleting(false);
    }
  };

  const itemClass =
    'dropdown-item px-16 py-8 rounded d-flex align-items-center gap-2 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900';

  return (
    <div className="card p-0 radius-12">
      <div className="card-body p-24">
        {/* Header */}
        <div className="mb-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-auto">
              <h5 className="mb-0 fw-semibold text-center text-md-start">
                Información Adicional
              </h5>
            </div>
            <div className="col-12 col-md">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Buscar formulario..."
                  minChars={3}
                  className="flex-grow-1"
                />
                <button
                  onClick={() => navigate('/cuestionarios/nuevo')}
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2 radius-8"
                  style={{ height: '44px', minWidth: '160px' }}
                >
                  <Icon icon="mdi:plus" width="20" />
                  <span className="d-none d-sm-inline fw-medium">Nuevo Formulario</span>
                  <span className="d-inline d-sm-none fw-medium">Nuevo</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <Loader center text="Cargando formularios..." delay={200} />
        ) : grupos.length === 0 ? (
          <EmptyState
            icon={search ? 'mdi:magnify-remove-outline' : 'mdi:file-document-outline'}
            title={search ? 'No se encontraron formularios' : 'No hay formularios registrados'}
            description={
              search
                ? 'Intenta con otra búsqueda'
                : 'Comienza creando un nuevo formulario de información adicional'
            }
          />
        ) : (
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table mb-0" style={{ minWidth: '700px' }}>
              <thead>
                <tr>
                  <th scope="col" style={{ minWidth: '220px' }}>Nombre y Descripción</th>
                  <th scope="col" style={{ minWidth: '100px' }}>Preguntas</th>
                  <th scope="col" style={{ minWidth: '100px' }}>Estado</th>
                  <th scope="col" style={{ minWidth: '80px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="text-md fw-medium text-primary-light">{g.nombre}</span>
                        {g.descripcion && (
                          <span className="text-xs text-secondary-light mt-1">{g.descripcion}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="px-3 py-1 rounded-pill fw-medium text-sm bg-neutral-100 text-secondary-light d-inline-flex align-items-center gap-1">
                        <Icon icon="mdi:format-list-bulleted" width="14" />
                        {g.total_preguntas || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`px-3 py-1 rounded-pill fw-medium text-sm ${
                          g.activo
                            ? 'bg-success-focus text-success-main'
                            : 'bg-danger-focus text-danger-main'
                        }`}
                      >
                        {g.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <span className="text-center text-neutral-700 text-xl">
                        <div className="dropdown text-center">
                          <button
                            type="button"
                            className="btn btn-sm"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <Icon
                              icon="ph:dots-three-outline-vertical-fill"
                              width="20"
                              className="text-neutral-700"
                            />
                          </button>
                          <ul className="dropdown-menu p-12 border bg-base shadow">
                            <li>
                              <button
                                className={itemClass}
                                onClick={() => navigate(`/cuestionarios/${g.id}`)}
                              >
                                <Icon icon="mdi:pencil-outline" width="18" />
                                <span>Editar</span>
                              </button>
                            </li>
                            <li>
                              <button
                                className={itemClass}
                                onClick={() => handleDuplicate(g.id)}
                              >
                                <Icon icon="mdi:content-copy" width="18" />
                                <span>Duplicar</span>
                              </button>
                            </li>
                            <li>
                              <button
                                className={`${itemClass} ${g.activo ? 'text-danger' : 'text-success'}`}
                                onClick={() => handleToggle(g.id)}
                              >
                                <Icon
                                  icon={g.activo ? 'mdi:toggle-switch-off-outline' : 'mdi:toggle-switch-outline'}
                                  width="18"
                                />
                                <span>{g.activo ? 'Desactivar' : 'Activar'}</span>
                              </button>
                            </li>
                            <li>
                              <button
                                className={`${itemClass} text-danger`}
                                onClick={() => openDeleteConfirm(g.id)}
                              >
                                <Icon icon="mdi:trash-can-outline" width="18" />
                                <span>Eliminar</span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal confirmar eliminar */}
      <Modal
        open={confirmOpen}
        onClose={() => !deleting && setConfirmOpen(false)}
        title="Eliminar formulario"
        size="md"
      >
        <div className="text-center py-3">
          <Icon
            icon="mdi:alert-circle"
            width="64"
            height="64"
            className="mb-3 text-warning"
          />
          <h5 className="mb-3">¿Estás seguro de eliminar este formulario?</h5>
          <p className="text-muted">
            Esta acción no se puede deshacer. Se eliminará el formulario y todas sus preguntas.
          </p>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmOpen(false)}
            disabled={deleting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger d-flex align-items-center gap-2"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader type="spinner" size="sm" delay={0} />
                Eliminando...
              </>
            ) : (
              <>
                <Icon icon="mdi:trash-can-outline" width="18" />
                Eliminar
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}
